/**
 * useResteem Hook
 *
 * Provides resteem functionality with TanStack Query mutation.
 * Includes RC check, cache invalidation, and resteem status check.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/authStore'
import { resteem, hasResteemed } from '@/services/steemit/resteemService'
import { getUserRC } from '@/services/steemit/rcService'

const MIN_RC_PERCENTAGE = 5 // Resteem requires very little RC

export interface UseResteemParams {
  author: string
  permlink: string
}

/**
 * Hook to check if the current user has already resteemed a post
 */
export function useHasResteemed({ author, permlink }: UseResteemParams) {
  const { user } = useAuthStore()

  return useQuery({
    queryKey: ['resteem-status', user?.username, author, permlink],
    queryFn: async () => {
      if (!user) return false
      return hasResteemed(user.username, author, permlink)
    },
    enabled: !!user && !!author && !!permlink,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Hook for resteeeming a post
 */
export function useResteem() {
  const queryClient = useQueryClient()
  const { user, updateRcPercentage } = useAuthStore()

  return useMutation({
    mutationFn: async (params: UseResteemParams) => {
      if (!user) {
        throw new Error('User not authenticated')
      }

      // Check RC before resteeeming
      if (user.rcPercentage < MIN_RC_PERCENTAGE) {
        throw new Error(
          `Insufficient Resource Credits. You need at least ${MIN_RC_PERCENTAGE}% RC to resteem. Current: ${user.rcPercentage.toFixed(1)}%`
        )
      }

      // Cannot resteem own posts
      if (user.username === params.author) {
        throw new Error('Cannot resteem your own posts')
      }

      const result = await resteem({
        account: user.username,
        postingKey: user.postingKey,
        author: params.author,
        permlink: params.permlink,
      })

      if (!result.success) {
        throw new Error(result.error || 'Failed to resteem')
      }

      // Refresh RC after resteeming
      try {
        const rc = await getUserRC(user.username)
        updateRcPercentage(rc.percentage)
      } catch (error) {
        console.error('Failed to refresh RC:', error)
      }

      return result
    },
    onSuccess: (_data, variables) => {
      // Invalidate resteem status query
      queryClient.setQueryData(
        ['resteem-status', user?.username, variables.author, variables.permlink],
        true
      )

      // Invalidate user's blog feed
      queryClient.invalidateQueries({ queryKey: ['user-posts'] })
      queryClient.invalidateQueries({ queryKey: ['blog'] })
    },
  })
}

/**
 * Hook to check if user can resteem a post
 */
export function useCanResteem({ author }: { author: string }) {
  const { user } = useAuthStore()

  if (!user) {
    return {
      canResteem: false,
      reason: 'Not logged in',
    }
  }

  if (user.username === author) {
    return {
      canResteem: false,
      reason: 'Cannot resteem your own posts',
    }
  }

  if (user.rcPercentage < MIN_RC_PERCENTAGE) {
    return {
      canResteem: false,
      reason: `Insufficient RC (${user.rcPercentage.toFixed(1)}%)`,
    }
  }

  return {
    canResteem: true,
    reason: null,
  }
}
